import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const UniversityFilter = ({ onUniversityChange, selectedUniversityId }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [universities, setUniversities] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';

  // Fetch all universities (for initial load or when search is empty)
  const fetchAllUniversities = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_BASE_URL}/admin/campus/universities`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUniversities(response.data.universities || []);
    } catch (error) {
      console.error('Error fetching universities:', error);
      setUniversities([]);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  // Search universities on server
  const searchUniversities = useCallback(async (query) => {
    if (!query || query.trim().length === 0) {
      fetchAllUniversities();
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_BASE_URL}/admin/campus/universities/search`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { query: query.trim(), limit: 50 }
      });
      setUniversities(response.data.universities || []);
    } catch (error) {
      console.error('Error searching universities:', error);
      setUniversities([]);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, fetchAllUniversities]);

  // Load all universities on mount
  useEffect(() => {
    fetchAllUniversities();
    // Default to "All Universities" on mount
    onUniversityChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced server-side search
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If search query is empty, fetch all universities
    if (searchQuery.trim().length === 0) {
      fetchAllUniversities();
      return;
    }

    // Debounce search - wait 300ms after user stops typing
    searchTimeoutRef.current = setTimeout(() => {
      searchUniversities(searchQuery);
    }, 300);

    // Cleanup timeout on unmount or when searchQuery changes
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, fetchAllUniversities, searchUniversities]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setIsOpen(true);
  };

  const handleSelectUniversity = (university) => {
    onUniversityChange(university);
    setSearchQuery(university ? university.name : '');
    setIsOpen(false);
  };

  const handleClearFilter = () => {
    onUniversityChange(null);
    setSearchQuery('');
    setIsOpen(false);
  };


  return (
    <div className="mb-4" style={{ maxWidth: '400px' }}>
      <label className="form-label fw-bold">Filter by University</label>
      <div className="position-relative" ref={dropdownRef}>
        <input
          type="text"
          className="form-control"
          placeholder="Search university..."
          value={searchQuery}
          onChange={handleSearch}
          onFocus={() => setIsOpen(true)}
        />
        {selectedUniversityId && (
          <button
            className="btn btn-sm btn-outline-secondary position-absolute"
            style={{ right: '5px', top: '5px' }}
            onClick={handleClearFilter}
          >
            Clear
          </button>
        )}
        
        {isOpen && (
          <div
            className="position-absolute w-100 bg-white border rounded shadow-lg"
            style={{
              maxHeight: '300px',
              overflowY: 'auto',
              zIndex: 1000,
              top: '100%',
              marginTop: '2px'
            }}
          >
            {loading ? (
              <div className="p-3 text-center">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <>
                <div
                  className="p-2 border-bottom cursor-pointer"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSelectUniversity(null)}
                >
                  <strong>All Universities</strong>
                </div>
                {universities.length === 0 ? (
                  <div className="p-3 text-muted text-center">No universities found</div>
                ) : (
                  universities.map(uni => (
                    <div
                      key={uni.id}
                      className="p-2 border-bottom hover-bg-light"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSelectUniversity(uni)}
                      onMouseEnter={(e) => e.target.classList.add('bg-light')}
                      onMouseLeave={(e) => e.target.classList.remove('bg-light')}
                    >
                      {uni.name}
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UniversityFilter;

